import React, { useState, useCallback, useEffect, useLayoutEffect } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Controls,
  Background,
  MarkerType,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  CloseOutlined as CloseIcon,
  AccountBalanceOutlined as BoardIcon,
  GroupsOutlined as CommitteeIcon,
  PersonOutlined as PersonIcon,
  BusinessCenterOutlined as ExecutiveIcon,
  ExpandMoreOutlined as ExpandIcon,
  ExpandLessOutlined as CollapseIcon,
  GavelOutlined as AuditIcon,
  SecurityOutlined as RiskIcon,
  HowToRegOutlined as NominationIcon,
  PaidOutlined as RemunerationIcon,
  AccountTreeOutlined as GovernanceIcon,
  TrendingUpOutlined as InvestmentIcon,
  MoreHorizOutlined as OtherIcon,
  VerticalAlignTopOutlined as VerticalIcon,
  VerticalAlignCenterOutlined as HorizontalIcon,
  StarOutlined as ChairIcon,
  EmailOutlined as EmailIcon,
  PhoneOutlined as PhoneIcon,
} from '@mui/icons-material';

// Committee type icons and colors
const committeeConfig = {
  Audit: { icon: AuditIcon, color: '#1976D2', bg: '#E3F2FD' },
  Risk: { icon: RiskIcon, color: '#D32F2F', bg: '#FFEBEE' },
  Nomination: { icon: NominationIcon, color: '#7B1FA2', bg: '#F3E5F5' },
  Remuneration: { icon: RemunerationIcon, color: '#388E3C', bg: '#E8F5E9' },
  Governance: { icon: GovernanceIcon, color: '#00796B', bg: '#E0F2F1' },
  Executive: { icon: ExecutiveIcon, color: '#F57C00', bg: '#FFF3E0' },
  Investment: { icon: InvestmentIcon, color: '#5D4037', bg: '#EFEBE9' },
  Other: { icon: OtherIcon, color: '#757575', bg: '#F5F5F5' },
};

// Officer position colors
const getPositionStyle = (position) => {
  const pos = position?.toLowerCase() || '';
  if (pos.includes('ceo') || pos.includes('chief executive')) {
    return { bg: '#1565C0', color: '#fff', icon: '👔' };
  } else if (pos.includes('cfo') || pos.includes('financial')) {
    return { bg: '#2E7D32', color: '#fff', icon: '💰' };
  } else if (pos.includes('coo') || pos.includes('operating')) {
    return { bg: '#E65100', color: '#fff', icon: '⚙️' };
  } else if (pos.includes('secretary')) {
    return { bg: '#6A1B9A', color: '#fff', icon: '📋' };
  } else if (pos.includes('legal') || pos.includes('counsel')) {
    return { bg: '#C62828', color: '#fff', icon: '⚖️' };
  } else if (pos.includes('compliance')) {
    return { bg: '#00695C', color: '#fff', icon: '✓' };
  } else if (pos.includes('cto') || pos.includes('technology')) {
    return { bg: '#283593', color: '#fff', icon: '💻' };
  } else if (pos.includes('cmo') || pos.includes('marketing')) {
    return { bg: '#AD1457', color: '#fff', icon: '📢' };
  } else if (pos.includes('chro') || pos.includes('human')) {
    return { bg: '#4527A0', color: '#fff', icon: '👥' };
  }
  return { bg: '#455A64', color: '#fff', icon: '👤' };
};

// ============ COMPACT CUSTOM NODES ============

// Board of Directors Node - Compact
const BoardNode = ({ data }) => {
  return (
    <div
      onClick={(e) => { if (data.onClick) { e.stopPropagation(); data.onClick(); } }}
      style={{
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
        borderRadius: 8,
        padding: '10px 14px',
        width: 140,
        boxShadow: '0 2px 8px rgba(26, 35, 126, 0.3)',
        cursor: 'pointer',
        textAlign: 'center',
      }}
    >
      <Handle type="source" position={Position.Bottom} style={{ background: '#3949ab' }} />
      
      <BoardIcon sx={{ color: '#fff', fontSize: 24, mb: 0.5 }} />
      <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.75rem', lineHeight: 1.2 }}>
        Board of Directors
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem' }}>
        {data.memberCount} members
      </Typography>
    </div>
  );
};

// Committee Node - Compact
const CommitteeNode = ({ data }) => {
  const config = committeeConfig[data.type] || committeeConfig.Other;
  const Icon = config.icon;

  // Shorten committee name
  const shortName = data.label
    ?.replace('Committee', '')
    ?.replace('& Remuneration', '')
    ?.replace('& Compliance', '')
    ?.trim() || data.label;

  return (
    <div
      onClick={(e) => { if (data.onClick) { e.stopPropagation(); data.onClick(); } }}
      style={{
        background: '#fff',
        borderRadius: 8,
        padding: '8px 12px',
        width: 110,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: `2px solid ${config.color}`,
        cursor: 'pointer',
        textAlign: 'center',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: config.color }} />
      <Handle type="source" position={Position.Bottom} style={{ background: config.color }} />

      <div style={{
        background: config.bg,
        borderRadius: 6,
        padding: 4,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 4,
      }}>
        <Icon sx={{ color: config.color, fontSize: 18 }} />
      </div>
      <Typography sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '0.7rem', lineHeight: 1.2 }}>
        {shortName}
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
        {data.memberCount} members
      </Typography>
    </div>
  );
};

// Executive/Officer Node - Compact
const ExecutiveNode = ({ data }) => {
  const style = getPositionStyle(data.position);
  
  // Get short title (e.g., "CEO", "CFO")
  const shortTitle = data.position
    ?.match(/\(([^)]+)\)/)?.[1] || // Extract from parentheses like "(CEO)"
    data.position?.split(' ').slice(-1)[0] || // Or last word
    data.position;

  return (
    <div
      onClick={(e) => { if (data.onClick) { e.stopPropagation(); data.onClick(); } }}
      style={{
        background: style.bg,
        borderRadius: 8,
        padding: '8px 12px',
        width: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        cursor: 'pointer',
        textAlign: 'center',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: style.bg }} />
      <Handle type="source" position={Position.Bottom} style={{ background: style.bg }} />

      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: 'rgba(255,255,255,0.2)',
          color: style.color,
          fontSize: '0.9rem',
          fontWeight: 600,
          margin: '0 auto 4px',
        }}
      >
        {data.name?.charAt(0)}
      </Avatar>
      <Typography sx={{ fontWeight: 600, color: style.color, fontSize: '0.7rem', lineHeight: 1.2 }}>
        {shortTitle}
      </Typography>
      <Typography sx={{ 
        color: 'rgba(255,255,255,0.8)', 
        fontSize: '0.6rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {data.name?.split(' ')[0]}
      </Typography>
    </div>
  );
};

// Node types registry
const nodeTypes = {
  board: BoardNode,
  committee: CommitteeNode,
  executive: ExecutiveNode,
};

// ============ CUSTOM TREE LAYOUT ENGINE ============
// Inspired by CorporateOwnershipChart for proper spacing

const NODE_DIMENSIONS = {
  board: { width: 150, height: 80 },
  committee: { width: 120, height: 80 },
  executive: { width: 110, height: 80 },
};
const GAP_X = 30;  // Horizontal gap between siblings
const GAP_Y = 60;  // Vertical gap between parent and children

const calculateTreeLayout = (nodes, edges, direction = 'TB') => {
  const isHorizontal = direction === 'LR';
  
  // Build adjacency map
  const childrenMap = new Map();
  const hasParent = new Set();
  
  edges.forEach(edge => {
    if (!childrenMap.has(edge.source)) childrenMap.set(edge.source, []);
    childrenMap.get(edge.source).push(edge.target);
    hasParent.add(edge.target);
  });

  // Find roots (nodes with no parent)
  const roots = nodes.filter(n => !hasParent.has(n.id)).map(n => n.id);

  // Create node map with dimensions
  const nodeMap = new Map();
  nodes.forEach(n => {
    const dim = NODE_DIMENSIONS[n.type] || { width: 110, height: 80 };
    nodeMap.set(n.id, { ...n, width: dim.width, height: dim.height });
  });

  // Calculate subtree sizes recursively
  const getSubtreeSize = (nodeId) => {
    const node = nodeMap.get(nodeId);
    if (!node) return { width: 0, height: 0 };

    const children = childrenMap.get(nodeId) || [];
    
    if (children.length === 0) {
      node.subtreeWidth = node.width;
      node.subtreeHeight = node.height;
      return { width: node.width, height: node.height };
    }

    // Calculate children sizes
    const childSizes = children.map(cid => getSubtreeSize(cid));

    let childrenWidth, childrenHeight;
    if (isHorizontal) {
      // Children stacked vertically
      childrenWidth = Math.max(...childSizes.map(s => s.width));
      childrenHeight = childSizes.reduce((sum, s) => sum + s.height, 0) + (childSizes.length - 1) * GAP_Y;
    } else {
      // Children arranged horizontally
      childrenWidth = childSizes.reduce((sum, s) => sum + s.width, 0) + (childSizes.length - 1) * GAP_X;
      childrenHeight = Math.max(...childSizes.map(s => s.height));
    }

    if (isHorizontal) {
      node.subtreeWidth = node.width + GAP_X + childrenWidth;
      node.subtreeHeight = Math.max(node.height, childrenHeight);
    } else {
      node.subtreeWidth = Math.max(node.width, childrenWidth);
      node.subtreeHeight = node.height + GAP_Y + childrenHeight;
    }

    node.childSizes = childSizes;
    node.children = children;

    return { width: node.subtreeWidth, height: node.subtreeHeight };
  };

  // Position nodes recursively
  const setPositions = (nodeId, x, y) => {
    const node = nodeMap.get(nodeId);
    if (!node) return;

    const children = node.children || [];

    if (isHorizontal) {
      // Parent on left, centered vertically
      node.position = { 
        x, 
        y: y + (node.subtreeHeight - node.height) / 2 
      };

      // Children on right, stacked vertically
      let currentY = y;
      children.forEach((childId, idx) => {
        const childNode = nodeMap.get(childId);
        setPositions(childId, x + node.width + GAP_X, currentY);
        currentY += (node.childSizes?.[idx]?.height || childNode.height) + GAP_Y;
      });
    } else {
      // Parent on top, centered horizontally
      node.position = { 
        x: x + (node.subtreeWidth - node.width) / 2, 
        y 
      };

      // Children below, arranged horizontally
      let currentX = x;
      children.forEach((childId, idx) => {
        const childNode = nodeMap.get(childId);
        setPositions(childId, currentX, y + node.height + GAP_Y);
        currentX += (node.childSizes?.[idx]?.width || childNode.width) + GAP_X;
      });
    }
  };

  // Layout each root
  let startX = 0;
  roots.forEach(rootId => {
    getSubtreeSize(rootId);
    setPositions(rootId, startX, 0);
    const rootNode = nodeMap.get(rootId);
    startX += (rootNode?.subtreeWidth || 0) + GAP_X * 2;
  });

  // Return layouted nodes
  const layoutedNodes = nodes.map(n => {
    const layouted = nodeMap.get(n.id);
    return {
      ...n,
      position: layouted?.position || { x: 0, y: 0 },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// Wrapper that uses the custom tree layout
const calculateLayout = (nodes, edges, direction = 'TB') => {
  return calculateTreeLayout(nodes, edges, direction);
};

// ============ SIDE PANEL ============

const DetailPanel = ({ selectedItem, onClose }) => {
  if (!selectedItem) return null;

  const { type, data } = selectedItem;

  return (
    <Paper
      sx={{
        position: 'absolute',
        right: 16,
        top: 16,
        bottom: 16,
        width: 320,
        zIndex: 10,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        background: type === 'board' 
          ? 'linear-gradient(135deg, #1a237e 0%, #283593 100%)'
          : type === 'committee'
            ? (committeeConfig[data.type]?.bg || '#F5F5F5')
            : getPositionStyle(data.position).bg,
        color: type === 'board' || type === 'executive' ? '#fff' : 'inherit',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'inherit' }}>
              {type === 'board' ? 'Board of Directors' : data.label || data.name}
            </Typography>
            {type === 'committee' && (
              <Chip
                label={data.type}
                size="small"
                sx={{ mt: 0.5, bgcolor: 'rgba(0,0,0,0.1)' }}
              />
            )}
            {type === 'executive' && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {data.position}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {/* Board Members */}
        {type === 'board' && data.directors && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Directors ({data.directors.length})
            </Typography>
            <List dense sx={{ mx: -1 }}>
              {data.directors.map((director, idx) => (
                <ListItem key={idx} sx={{ px: 1 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: '#E3F2FD', color: '#1976D2' }}>
                      {director.name?.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={director.name}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        <Chip 
                          label={director.position} 
                          size="small" 
                          sx={{ height: 18, fontSize: '0.65rem' }} 
                        />
                        {director.isIndependent && (
                          <Chip 
                            label="Independent" 
                            size="small" 
                            sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#E8F5E9', color: '#2E7D32' }} 
                          />
                        )}
                      </Box>
                    }
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {/* Committee Members */}
        {type === 'committee' && data.members && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Members ({data.members.length})
            </Typography>
            <List dense sx={{ mx: -1 }}>
              {data.members.map((member, idx) => {
                const isChair = member.role === 'CHAIRPERSON' || member.role === 'CHAIRMAN';
                return (
                  <ListItem key={idx} sx={{ px: 1 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ 
                        width: 36, 
                        height: 36, 
                        bgcolor: isChair ? '#FFF3E0' : '#F5F5F5',
                        color: isChair ? '#E65100' : '#757575',
                      }}>
                        {isChair ? <ChairIcon fontSize="small" /> : member.name?.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {member.name}
                          {isChair && (
                            <Chip 
                              label="Chair" 
                              size="small" 
                              sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#FFF3E0', color: '#E65100' }} 
                            />
                          )}
                        </Box>
                      }
                      secondary={member.role !== 'CHAIRPERSON' && member.role !== 'CHAIRMAN' ? member.role : null}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                );
              })}
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Committee Details */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Committee Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {data.meetingFrequency && (
                <Typography variant="body2" color="textSecondary">
                  <strong>Meeting Frequency:</strong> {data.meetingFrequency}
                </Typography>
              )}
              {data.quorumType && (
                <Typography variant="body2" color="textSecondary">
                  <strong>Quorum:</strong> {data.quorumType === 'PERCENTAGE' 
                    ? `${data.quorumPercentage}%` 
                    : `${data.quorumNumber} members`}
                </Typography>
              )}
            </Box>
          </>
        )}

        {/* Executive Details */}
        {type === 'executive' && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Contact Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {data.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">{data.email}</Typography>
                </Box>
              )}
              {data.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">{data.phone}</Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Appointment Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {data.appointmentDate && (
                <Typography variant="body2" color="textSecondary">
                  <strong>Appointed:</strong> {new Date(data.appointmentDate).toLocaleDateString('en-GB')}
                </Typography>
              )}
              {data.authorityLevel && (
                <Typography variant="body2" color="textSecondary">
                  <strong>Authority Level:</strong> {data.authorityLevel}
                </Typography>
              )}
              {data.isAuthorizedSignatory && (
                <Chip
                  label="Authorized Signatory"
                  size="small"
                  sx={{ alignSelf: 'flex-start', bgcolor: '#E8F5E9', color: '#2E7D32' }}
                />
              )}
            </Box>

            {data.directReports && data.directReports.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Direct Reports ({data.directReports.length})
                </Typography>
                <List dense sx={{ mx: -1 }}>
                  {data.directReports.map((report, idx) => (
                    <ListItem key={idx} sx={{ px: 1 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#F5F5F5' }}>
                          {report.name?.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={report.name}
                        secondary={report.position}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const committeeMembers = (committee) => {
  if (Array.isArray(committee?.members)) return committee.members;
  const chairperson = committee?.chairperson || committee?.chairperson_name;
  return chairperson ? [{ name: chairperson, role: 'CHAIRPERSON' }] : [];
};

// ============ MAIN FLOW COMPONENT ============

const GovernanceFlow = ({ 
  boardData,
  committees,
  officers,
  height = 600,
}) => {
  const safeCommittees = asArray(committees);
  const safeOfficers = asArray(officers);
  const [selectedItem, setSelectedItem] = useState(null);
  const [layoutDirection, setLayoutDirection] = useState('TB');
  const [layoutKey, setLayoutKey] = useState(0);

  // Build nodes and edges from data
  const buildGraphData = useCallback(() => {
    const nodes = [];
    const edges = [];

    // 1. Board of Directors node
    nodes.push({
      id: 'board',
      type: 'board',
      position: { x: 0, y: 0 },
      data: {
        label: 'Board of Directors',
        memberCount: boardData?.directors?.length || 0,
        independentCount: boardData?.directors?.filter(d => d.isIndependent).length || 0,
        chairman: boardData?.chairman,
        directors: boardData?.directors || [],
        onClick: () => setSelectedItem({ type: 'board', data: boardData }),
      },
    });

    // 2. Find CEO first (to structure the layout properly)
    const ceo = safeOfficers.find(o => 
      o.position?.toLowerCase().includes('ceo') || 
      o.position?.toLowerCase().includes('chief executive')
    );

    // 3. Add CEO node directly under board (before committees to establish hierarchy)
    if (ceo) {
      const ceoNodeId = `officer-${ceo.id}`;
      
      // Get direct reports to CEO
      const directReports = safeOfficers.filter(o => o.reportsTo === ceo.id || 
        (!o.reportsTo && o.id !== ceo.id && (
          o.position?.toLowerCase().includes('cfo') ||
          o.position?.toLowerCase().includes('coo') ||
          o.position?.toLowerCase().includes('secretary') ||
          o.position?.toLowerCase().includes('cto') ||
          o.position?.toLowerCase().includes('cmo') ||
          o.position?.toLowerCase().includes('chro')
        ))
      );

      nodes.push({
        id: ceoNodeId,
        type: 'executive',
        position: { x: 0, y: 0 },
        data: {
          ...ceo,
          directReports,
          onClick: () => setSelectedItem({ type: 'executive', data: { ...ceo, directReports } }),
        },
      });

      // Connect board to CEO
      edges.push({
        id: `board-to-ceo`,
        source: 'board',
        target: ceoNodeId,
        type: 'smoothstep',
        style: { stroke: '#1565C0', strokeWidth: 3 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#1565C0' },
        label: 'Appoints',
        labelStyle: { fontSize: 10, fill: '#666' },
        labelBgStyle: { fill: '#fff' },
      });

      // 4. Add other executives (report to CEO)
      const otherOfficers = safeOfficers.filter(o => o.id !== ceo.id);
      otherOfficers.forEach((officer) => {
        const officerNodeId = `officer-${officer.id}`;
        nodes.push({
          id: officerNodeId,
          type: 'executive',
          position: { x: 0, y: 0 },
          data: {
            ...officer,
            onClick: () => setSelectedItem({ type: 'executive', data: officer }),
          },
        });

        // Connect to CEO
        edges.push({
          id: `ceo-to-${officerNodeId}`,
          source: ceoNodeId,
          target: officerNodeId,
          type: 'smoothstep',
          style: { stroke: '#757575', strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#757575' },
        });
      });
    }

    // 5. Committee nodes - connected to board separately
    // We'll position them to the left side by using a committee group structure
    safeCommittees.forEach((committee, index) => {
      const nodeId = `committee-${committee.id}`;
      const members = committeeMembers(committee);
      nodes.push({
        id: nodeId,
        type: 'committee',
        position: { x: 0, y: 0 },
        data: {
          label: committee.name,
          type: committee.type,
          status: committee.status,
          memberCount: members.length || committee.memberCount || 0,
          chairperson: members.find(m => m.role === 'CHAIRPERSON' || m.role === 'CHAIRMAN')?.name
            || committee.chairperson,
          members,
          meetingFrequency: committee.meetingFrequency,
          quorumType: committee.quorumType,
          quorumPercentage: committee.quorumPercentage,
          quorumNumber: committee.quorumNumber,
          onClick: () => setSelectedItem({ type: 'committee', data: { ...committee, label: committee.name } }),
        },
      });

      // Connect board to committee
      edges.push({
        id: `board-to-${nodeId}`,
        source: 'board',
        target: nodeId,
        type: 'smoothstep',
        style: { stroke: '#9E9E9E', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#9E9E9E' },
      });
    });

    return { nodes, edges };
  }, [boardData, safeCommittees, safeOfficers]);

  const { nodes: initialNodes, edges: initialEdges } = buildGraphData();

  // Apply initial layout
  const getLayoutedData = useCallback(() => {
    return calculateLayout(initialNodes, initialEdges, layoutDirection);
  }, [initialNodes, initialEdges, layoutDirection]);

  const [nodes, setNodes, onNodesChange] = useNodesState(getLayoutedData().nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(getLayoutedData().edges);
  const { fitView } = useReactFlow();

  // Update layout when direction changes or data changes
  useEffect(() => {
    const { nodes: graphNodes, edges: graphEdges } = buildGraphData();
    const { nodes: layoutedNodes, edges: layoutedEdges } = calculateLayout(graphNodes, graphEdges, layoutDirection);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    setLayoutKey(k => k + 1);
  }, [layoutDirection, buildGraphData, setNodes, setEdges]);

  // Fit view after layout
  useEffect(() => {
    const timer = setTimeout(() => {
      fitView({ padding: 0.2, duration: 300 });
    }, 100);
    return () => clearTimeout(timer);
  }, [layoutKey, fitView]);

  // Default edge options with rounded corners for smoothstep
  const defaultEdgeOptions = {
    type: 'smoothstep',
    pathOptions: { borderRadius: 20 },
  };

  return (
    <Box sx={{ 
      position: 'relative', 
      height, 
      bgcolor: '#fafafa', 
      borderRadius: 2, 
      overflow: 'hidden',
      '& .react-flow__attribution': {
        display: 'none',
      },
    }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        minZoom={0.3}
        maxZoom={1.5}
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
        onPaneClick={() => setSelectedItem(null)}
      >
        <Background color="#e0e0e0" gap={20} />
        <Controls showInteractive={false} />
      </ReactFlow>

      {/* Layout Toggle */}
      <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 10 }}>
        <Paper sx={{ p: 0.5 }}>
          <ToggleButtonGroup
            value={layoutDirection}
            exclusive
            onChange={(e, val) => val && setLayoutDirection(val)}
            size="small"
          >
            <ToggleButton value="TB">
              <Tooltip title="Vertical Layout">
                <VerticalIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="LR">
              <Tooltip title="Horizontal Layout">
                <HorizontalIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Paper>
      </Box>

      {/* Detail Panel */}
      <DetailPanel selectedItem={selectedItem} onClose={() => setSelectedItem(null)} />
    </Box>
  );
};

// ============ EXPORTED COMPONENT ============

const GovernanceStructureChart = (props) => {
  return (
    <ReactFlowProvider>
      <GovernanceFlow {...props} />
    </ReactFlowProvider>
  );
};

export default GovernanceStructureChart;
